("use strict");

(function initFreeGift(window) {
  const HIDDEN_GIFT_PROPERTY = "_auto_gift";

  const state = {
    isSyncing: false,
    pending: false,
    latestOptions: {},
    activePromise: Promise.resolve({ changed: false }),
  };

  function toNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function toBoolean(value, fallback = false) {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["false", "0", "no", "off"].includes(normalized)) return false;
      if (["true", "1", "yes", "on"].includes(normalized)) return true;
    }
    return Boolean(value);
  }

  function firstDefined(...values) {
    for (const value of values) {
      if (value !== null && value !== undefined && value !== "") return value;
    }
    return null;
  }

  function getThemeSettings() {
    return window.themeSettings || {};
  }

  function getConfig() {
    const settings = getThemeSettings();

    const variantId = toNumber(
      firstDefined(
        settings.giftProductVariantId,
        settings.gift_product_variant_id,
        settings.giftVariantId,
        settings.giftProductId,
        settings.gift_product_id,
        window.headerGiftProduct && window.headerGiftProduct.variantId
      )
    );

    const thresholdCents = toNumber(
      firstDefined(
        settings.giftThresholdCents,
        settings.gift_threshold_cents,
        settings.giftMinimumAmountCents,
        settings.giftMinimumAmount,
        settings.minGiftAmountCents
      )
    );

    const enabledRaw = firstDefined(
      settings.enableAutoGift,
      settings.enable_auto_gift,
      settings.autoGiftEnabled
    );

    const normalizedThresholdCents = Number.isFinite(thresholdCents)
      ? Math.max(100, Math.round(thresholdCents))
      : 20000;

    return {
      enabled: toBoolean(enabledRaw, true),
      giftVariantId: variantId,
      giftThresholdCents: normalizedThresholdCents,
      propertyKey: HIDDEN_GIFT_PROPERTY,
      propertyValue: "1",
    };
  }

  async function fetchCart() {
    const response = await fetch("/cart.js");
    if (!response.ok) throw new Error("Nao foi possivel carregar /cart.js");
    return response.json();
  }

  function getGiftLines(cart, config) {
    if (!Array.isArray(cart?.items) || !config.giftVariantId) return [];
    return cart.items.filter((item) => {
      const propertyValue = item.properties?.[config.propertyKey];
      return item.variant_id === config.giftVariantId || String(propertyValue) === config.propertyValue;
    });
  }

  function getSubtotalWithoutGift(cart, giftLines) {
    if (!Array.isArray(cart?.items)) return 0;
    const giftLineKeys = new Set(giftLines.map((item) => item.key));
    return cart.items.reduce((sum, item) => {
      if (giftLineKeys.has(item.key)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  async function mutateCart(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Falha na mutacao de carrinho em ${url}`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) return response.json();
    const text = await response.text();
    return JSON.parse(text);
  }

  async function addGift(config, options) {
    const body = {
      id: config.giftVariantId,
      quantity: 1,
      properties: {
        [config.propertyKey]: config.propertyValue,
      },
    };
    if (Array.isArray(options.sections) && options.sections.length > 0) {
      body.sections = options.sections;
      body.sections_url = options.sectionsUrl || window.location.pathname;
    }
    return mutateCart(window.routes?.cart_add_url || "/cart/add.js", body);
  }

  async function changeLineQuantity(lineKey, quantity, options) {
    const body = {
      id: lineKey,
      quantity,
    };
    if (Array.isArray(options.sections) && options.sections.length > 0) {
      body.sections = options.sections;
      body.sections_url = options.sectionsUrl || window.location.pathname;
    }
    return mutateCart(window.routes?.cart_change_url || "/cart/change.js", body);
  }

  async function performSync(options = {}) {
    const config = getConfig();

    if (!config.enabled || !config.giftVariantId) {
      return { changed: false, cart: null, reason: "disabled_or_invalid_config" };
    }

    let cart = await fetchCart();
    let giftLines = getGiftLines(cart, config);

    const subtotalWithoutGift = getSubtotalWithoutGift(cart, giftLines);
    const nonGiftCount = (cart.items?.length || 0) - giftLines.length;
    const eligible = nonGiftCount > 0 && subtotalWithoutGift >= config.giftThresholdCents;

    let changed = false;
    let lastMutationState = null;

    if (!eligible && giftLines.length > 0) {
      for (const line of giftLines) {
        lastMutationState = await changeLineQuantity(line.key, 0, options);
        changed = true;
      }
    }

    if (eligible) {
      if (giftLines.length === 0) {
        lastMutationState = await addGift(config, options);
        changed = true;
      } else {
        const [firstLine, ...extraLines] = giftLines;
        for (const line of extraLines) {
          lastMutationState = await changeLineQuantity(line.key, 0, options);
          changed = true;
        }
        if (Number(firstLine.quantity) !== 1) {
          lastMutationState = await changeLineQuantity(firstLine.key, 1, options);
          changed = true;
        }
      }
    }

    if (!changed) {
      return { changed: false, cart, mutationState: null };
    }

    cart = await fetchCart();

    if (typeof options.onCartUpdated === "function") {
      options.onCartUpdated(cart);
    }

    if (lastMutationState?.sections && typeof options.onSectionsUpdated === "function") {
      options.onSectionsUpdated(lastMutationState.sections, cart);
    }

    return { changed: true, cart, mutationState: lastMutationState };
  }

  async function drainQueue() {
    state.isSyncing = true;
    let result = { changed: false };

    while (state.pending) {
      state.pending = false;
      const options = state.latestOptions || {};
      try {
        result = await performSync(options);
      } catch (error) {
        if (typeof options.onSyncError === "function") {
          options.onSyncError(error);
        } else {
          console.error("[BlsFreeGift] sync error", error);
        }
      }
    }

    state.isSyncing = false;
    return result;
  }

  function sync(options = {}) {
    state.latestOptions = options;
    state.pending = true;
    if (!state.isSyncing) {
      state.activePromise = drainQueue();
    }
    return state.activePromise;
  }

  function updateCartCount(cart) {
    if (cart?.item_count == null) return;
    document.querySelectorAll(".cart-count").forEach((el) => {
      el.innerHTML = el.classList.contains("cart-count-drawer")
        ? `(${cart.item_count})`
        : cart.item_count;
    });
    if (document.querySelector("header-total-price")) {
      document.querySelector("header-total-price").updateTotal(cart);
    }
  }

  function applyMiniCartSection(sections) {
    if (!sections?.["form-mini-cart"]) return;
    const miniCartRoot = document.getElementById("form-mini-cart");
    if (!miniCartRoot) return;
    const miniCartHtml = new DOMParser().parseFromString(sections["form-mini-cart"], "text/html");
    const newMiniCart = miniCartHtml.querySelector("#form-mini-cart");
    if (newMiniCart) miniCartRoot.innerHTML = newMiniCart.innerHTML;
  }

  function initialSync() {
    const sections = ["form-mini-cart"];
    const mainSection = document.getElementById("main-cart-items")?.dataset?.id;
    if (mainSection) sections.push(mainSection);
    sync({
      sections,
      sectionsUrl: window.location.pathname,
      onCartUpdated: updateCartCount,
      onSectionsUpdated: applyMiniCartSection,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialSync);
  } else {
    initialSync();
  }

  window.BlsFreeGift = {
    getConfig,
    sync,
    propertyKey: HIDDEN_GIFT_PROPERTY,
  };
})(window);
